import { CheckIcon } from '@/components/icons'
import { APP_NAME, chargeeAppUrl } from '@/lib/branding'
import type { ChecklistDevice, ChecklistItem } from '@/lib/programs'

const HEALTH_DOT: Record<ChecklistDevice['health'], string> = {
  online: 'bg-green',
  stale: 'bg-warning',
  offline: 'bg-red',
  never: 'bg-beige-2',
}

const HEALTH_TEXT: Record<ChecklistDevice['health'], string> = {
  online: 'text-green',
  stale: 'text-warning',
  offline: 'text-red',
  never: 'text-dark-blue/50',
}

const MISSING_NAME: Record<string, string> = { ev: 'auto', charger: 'laadpaal', p1: 'Sparky of Flint' }

function StatusCircle({ ok, optional }: { ok: boolean; optional: boolean }) {
  if (ok) {
    return (
      <span className="flex size-8 shrink-0 items-center justify-center rounded-full bg-light-green">
        <CheckIcon className="size-4" />
      </span>
    )
  }
  return optional ? (
    <span
      className="flex size-8 shrink-0 items-center justify-center rounded-full bg-beige text-15 font-bold text-dark-blue/40"
      aria-hidden="true"
    >
      –
    </span>
  ) : (
    <span className="size-8 shrink-0 rounded-full border-2 border-beige-2 bg-white" aria-hidden="true" />
  )
}

/** Step 2 of the sign-up: every car, charger and P1 reader at the address, grouped. A charger is optional. */
export function EligibilityChecklist({
  items,
  onRecheck,
  rechecking,
}: {
  items: ChecklistItem[]
  onRecheck: () => void
  rechecking: boolean
}) {
  return (
    <ul className="divide-y divide-beige-2 rounded-20 border border-beige-2">
      {items.map((item) => (
        <li key={item.id} className="flex items-start gap-4 p-4 md:px-5">
          <StatusCircle ok={item.ok} optional={item.optional} />
          <div className="min-w-0 flex-1">
            <p className="text-base font-bold">
              {item.title}
              <span className="sr-only">
                {item.ok ? ' (in orde)' : item.optional ? ' (niet gekoppeld, niet nodig)' : ' (nog niet in orde)'}
              </span>
            </p>
            {item.devices.length > 0 && (
              <ul className="mt-1.5 flex flex-col gap-1">
                {item.devices.map((d) => (
                  <li key={d.id} className="flex flex-wrap items-center gap-x-2 text-15">
                    <span className={`size-2 shrink-0 rounded-full ${HEALTH_DOT[d.health]}`} aria-hidden="true" />
                    <span className="font-semibold">{d.name}</span>
                    <span className={HEALTH_TEXT[d.health]}>{d.status}</span>
                  </li>
                ))}
              </ul>
            )}
            {(item.ok || item.optional) && item.note && (
              <p className="mt-1.5 text-15 text-dark-blue/60">{item.note}</p>
            )}
            {!item.ok && !item.optional && (
              <>
                <p className="mt-1.5 text-15 text-dark-blue/60">
                  {item.devices.length > 0 ? 'Geen werkende koppeling.' : 'Nog niet gekoppeld.'} Koppel je{' '}
                  {MISSING_NAME[item.id]} in de {APP_NAME} en controleer opnieuw.
                </p>
                <div className="mt-3 flex flex-wrap gap-2">
                  <a
                    href={chargeeAppUrl()}
                    target="_blank"
                    rel="noreferrer"
                    className="btn-cta !h-10 !px-4 !text-13"
                  >
                    Open {APP_NAME}
                  </a>
                  <button
                    type="button"
                    className="btn-secondary !h-10 !px-4 !text-13"
                    onClick={onRecheck}
                    disabled={rechecking}
                  >
                    {rechecking ? 'Controleren…' : 'Opnieuw controleren'}
                  </button>
                </div>
              </>
            )}
          </div>
        </li>
      ))}
    </ul>
  )
}
